import { useEffect, useRef, useState } from "react"
import { useMutation, useQuery } from "react-query"

import {
  cancelInvoicePayment,
  getInvoiceStatus,
} from "src/api/Billing/Payments"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import {
  BLUETOOTH_CARD_READER_ID,
  CARD_PRESENT_POLL_INTERVAL,
  CARD_PRESENT_POLL_TIMEOUT,
} from "../constants"

const useCardReaderPayment = ({
  invoiceId,
  marinaSlug,
  cardReaderId,
  enableBluetoothCardReader = false,
  isSelected = false,
  events,
  submit,
  readReaderUnconfirmed = (response) => response?.data?.readerUnconfirmed,
  onSettled,
  onError,
  genericErrorMessage,
  copy,
}) => {
  const [processingState, setProcessingState] = useState("idle")
  const [isReaderUnconfirmed, setIsReaderUnconfirmed] = useState(false)
  // A poll and a refused cancel's status read can both deliver the outcome.
  const isResolvedRef = useRef(false)
  const showToast = useToast()
  const tracker = useTracker()
  const isCollecting = processingState === "collecting"
  const isTimedOut = processingState === "timedOut"

  // Card-present is async: create the txn, the customer taps at the reader, a
  // webhook settles the invoice. Hold the caller open and poll until it resolves.
  const { mutate: start, isLoading: isStarting } = useMutation({
    mutationFn: submit,
    onSuccess: (response) => {
      // The native SDK collects via the client_secret; without it there's
      // nothing to hand off, so error rather than hang in "collecting".
      const collectingViaBluetooth =
        enableBluetoothCardReader && cardReaderId === BLUETOOTH_CARD_READER_ID
      if (collectingViaBluetooth) {
        if (!response?.clientSecret) {
          onError(genericErrorMessage)
          return
        }
        window.location.href = `dockwa://payment_intents/${response.clientSecret}`
      }
      isResolvedRef.current = false
      setIsReaderUnconfirmed(Boolean(readReaderUnconfirmed(response)))
      setProcessingState("collecting")
    },
    onError: (error) => {
      onError(
        error?.status === 503 ? copy.unavailable : genericErrorMessage,
        error
      )
    },
  })

  const handleStatus = (data) => {
    if (isResolvedRef.current) {
      return
    }
    if (data.display_status === "Processing") {
      setIsReaderUnconfirmed(Boolean(data.reader_unconfirmed))
      return
    }
    isResolvedRef.current = true
    setIsReaderUnconfirmed(false)
    if (data.display_status === "Settled") {
      tracker.trackEvent(events.processed, { result: "settled" })
      setProcessingState("idle")
      onSettled()
    } else {
      tracker.trackEvent(events.processed, { result: "declined" })
      setProcessingState("failed")
      onError(data.reader_failure_message ?? copy.declined)
    }
  }

  useQuery({
    queryKey: ["invoiceStatus", invoiceId],
    queryFn: () => getInvoiceStatus({ invoiceId, marinaSlug }),
    enabled: isCollecting && Boolean(invoiceId),
    refetchInterval: CARD_PRESENT_POLL_INTERVAL,
    refetchOnWindowFocus: false,
    onSuccess: handleStatus,
  })

  const showCancelTooLate = () =>
    showToast(copy.cancelTooLate, { type: "error" })

  const { mutate: cancel, isLoading: isCanceling } = useMutation({
    mutationFn: () => cancelInvoicePayment({ invoiceId, marinaSlug }),
    onSuccess: () => {
      isResolvedRef.current = true
      tracker.trackEvent(events.processed, { result: "canceled" })
      setProcessingState("idle")
    },
    onError: (error) => {
      if (error?.status !== 400) {
        onError(genericErrorMessage, error)
        return
      }
      // A refused cancel can mean the payment already failed, not that the customer
      // presented a card, so the status is read before the cashier is told to wait.
      getInvoiceStatus({ invoiceId, marinaSlug })
        .then((data) =>
          data.display_status === "Processing"
            ? showCancelTooLate()
            : handleStatus(data)
        )
        .catch(showCancelTooLate)
    },
  })

  // Give up after a few minutes so a stuck reader doesn't poll forever.
  useEffect(() => {
    if (!isCollecting) {
      return undefined
    }
    const timeoutId = setTimeout(() => {
      tracker.trackEvent(events.processed, { result: "timed_out" })
      setProcessingState("timedOut")
    }, CARD_PRESENT_POLL_TIMEOUT)
    return () => clearTimeout(timeoutId)
  }, [isCollecting, tracker, events.processed])

  // Fires once per selection, so drop-off before submit is measurable.
  useEffect(() => {
    if (isSelected) {
      tracker.trackEvent(events.selected)
    }
  }, [isSelected, tracker, events.selected])

  return {
    start,
    cancel: () => cancel(),
    reset: () => setProcessingState("idle"),
    processingState,
    isCollecting,
    isReaderUnconfirmed: isCollecting && isReaderUnconfirmed,
    isTimedOut,
    isProcessingView: isCollecting || isTimedOut,
    isStarting,
    isCanceling,
  }
}

export default useCardReaderPayment
